import { ActionPlanSourceFilterValue } from 'components/ActionPlans/constants';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import {
  ActionPlanCompanyFocusAreaFiltersType,
  ActionPlanCompanyFocusAreaRowType,
  ActionPlanCompanyOverallFiltersType,
  ActionPlanCompanyOverallRowType,
  ActionPlanCompanyPortfolioStatsType,
  ActionPlanCompanyStatsType,
  ActionPlanCreatePayloadType,
  ActionPlanDetailsType,
  ActionPlanEmailCampaignDetailType,
  ActionPlanEmailCampaignMessageType,
  ActionPlanEmailCampaignMessageUpsertPayloadType,
  ActionPlanEmailCampaignUpdatePayloadType,
  ActionPlanFocusAreaType,
  ActionPlanListItemType,
  ActionPlanMetricsType,
  ActionPlanOwnerUserType,
  ActionPlanPatchPayloadType,
  ActionPlanStepUpdatePayloadType,
} from 'types/actionPlans';
import { CommentPayloadType, CommentType } from 'types/comments';
import { PaginatedResponseType } from 'types/common';

import axios from '../config/axiosPrivate';

export type GetCommunityActionPlansParams = {
  page: number;
  page_size: number;
  source?: ACTION_PLAN_SOURCE;
  search?: string;
  source_categories?: ActionPlanSourceFilterValue[];
  alert_type?: number[];
  focus_area?: number[];
  owner?: number[];
  status?: number[];
};

export const getCommunityActionPlans = async (
  communityId: number,
  params: GetCommunityActionPlansParams
): Promise<PaginatedResponseType<ActionPlanListItemType>> => {
  const response = await axios.get(`/action_plans/communities/${communityId}`, {
    params,
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
};

export const getActionPlansMetrics = async (communityId: number): Promise<ActionPlanMetricsType> => {
  const response = await axios.get(`/action_plans/communities/${communityId}/metrics`);
  return response.data;
};

export const getActionPlanOwners = async (communityId: number): Promise<ActionPlanOwnerUserType[]> => {
  const response = await axios.get(`/action_plans/communities/${communityId}/owners`);
  return response.data;
};

export const getCompanyActionPlansOverall = async (
  companyId: number,
  filters: ActionPlanCompanyOverallFiltersType
): Promise<PaginatedResponseType<ActionPlanCompanyOverallRowType>> => {
  const response = await axios.get(`/action_plans/company/overall`, {
    params: { company_id: companyId, ...filters },
  });
  return response.data;
};

export const getCompanyActionPlansFocusArea = async (
  companyId: number,
  focusAreaId: number,
  filters: ActionPlanCompanyFocusAreaFiltersType
): Promise<PaginatedResponseType<ActionPlanCompanyFocusAreaRowType>> => {
  const response = await axios.get(`/action_plans/company/focus-area`, {
    params: { company_id: companyId, focus_area: focusAreaId, ...filters },
  });
  return response.data;
};

export const getCompanyActionPlansStats = async (
  companyId: number,
  focusAreaId?: number
): Promise<ActionPlanCompanyStatsType> => {
  const response = await axios.get(`/action_plans/company/stats`, {
    params: { company_id: companyId, ...(focusAreaId ? { focus_area: focusAreaId } : {}) },
  });
  return response.data;
};

export const getCompanyActionPlanStats = async (
  companyId: number,
  communityIds?: number[]
): Promise<ActionPlanCompanyPortfolioStatsType> => {
  const response = await axios.get(`/action_plans/company/portfolio-stats`, {
    params: { company_id: companyId, community_ids: communityIds?.length ? communityIds : undefined },
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
};

export const getActionPlanFocusAreas = async (): Promise<ActionPlanFocusAreaType[]> => {
  const response = await axios.get<ActionPlanFocusAreaType[]>('/action_plans/focus-areas');
  return response.data;
};

export const getActionPlanDetails = async (actionPlanId: number): Promise<ActionPlanDetailsType> => {
  const response = await axios.get<ActionPlanDetailsType>(`/action_plans/${actionPlanId}/details`);
  return response.data;
};

export const getActionPlanComments = async (actionPlanId: number): Promise<CommentType[]> => {
  const response = await axios.get<CommentType[]>(`/action_plans/${actionPlanId}/comments`);
  return response.data;
};

export const postActionPlanComment = async (
  actionPlanId: number,
  payload: CommentPayloadType
): Promise<CommentType> => {
  const response = await axios.post<CommentType>(`/action_plans/${actionPlanId}/comments`, payload);
  return response.data;
};

export const updateActionPlanComment = async (
  actionPlanId: number,
  commentId: number,
  payload: CommentPayloadType
): Promise<CommentType> => {
  const response = await axios.patch<CommentType>(`/action_plans/${actionPlanId}/comments/${commentId}`, payload);
  return response.data;
};

export const deleteActionPlanComment = async (actionPlanId: number, commentId: number) => {
  const response = await axios.delete(`/action_plans/${actionPlanId}/comments/${commentId}`);
  return response.data;
};

export const getActionPlanEmailCampaignDetails = async (
  actionPlanId: number
): Promise<ActionPlanEmailCampaignDetailType> => {
  const response = await axios.get<ActionPlanEmailCampaignDetailType>(`/action_plans/${actionPlanId}/email-campaign`);
  return response.data;
};

export const patchActionPlanEmailCampaignDetails = async (
  actionPlanId: number,
  payload: ActionPlanEmailCampaignUpdatePayloadType
): Promise<ActionPlanEmailCampaignDetailType> => {
  const response = await axios.patch<ActionPlanEmailCampaignDetailType>(
    `/action_plans/${actionPlanId}/email-campaign`,
    payload
  );
  return response.data;
};

export const createActionPlanEmailCampaignMessage = async (
  emailCampaignId: number,
  payload: ActionPlanEmailCampaignMessageUpsertPayloadType
): Promise<ActionPlanEmailCampaignMessageType> => {
  const response = await axios.post<ActionPlanEmailCampaignMessageType>(
    `/action_plans/email-campaigns/${emailCampaignId}/messages`,
    payload
  );
  return response.data;
};

export const patchActionPlanEmailCampaignMessage = async (
  messageId: number,
  payload: ActionPlanEmailCampaignMessageUpsertPayloadType
): Promise<ActionPlanEmailCampaignMessageType> => {
  const response = await axios.patch<ActionPlanEmailCampaignMessageType>(
    `/action_plans/email-campaigns/messages/${messageId}`,
    payload
  );
  return response.data;
};

export const deleteActionPlanEmailCampaignMessage = async (messageId: number) => {
  const response = await axios.delete(`/action_plans/email-campaigns/messages/${messageId}/delete`);
  return response.data;
};

export const createActionPlan = async (payload: ActionPlanCreatePayloadType) => {
  const response = await axios.post('/action_plans/', payload);
  return response.data;
};

export const deleteActionPlan = async (
  actionPlanId: number,
  payload?: {
    archived_reason?: string | null;
  }
) => {
  const response = await axios.delete(`/action_plans/${actionPlanId}/delete`, payload ? { data: payload } : undefined);
  return response.data;
};

export const patchActionPlan = async (actionPlanId: number, payload: ActionPlanPatchPayloadType) => {
  const response = await axios.patch(`/action_plans/${actionPlanId}`, payload);
  return response.data;
};

export const updateActionPlanStep = async (actionPlanStepId: number, payload: ActionPlanStepUpdatePayloadType) => {
  const response = await axios.patch(`/action_plans/steps/${actionPlanStepId}`, payload);
  return response.data;
};

export const deleteActionPlanStep = async (actionPlanStepId: number) => {
  const response = await axios.delete(`/action_plans/steps/${actionPlanStepId}/delete`);
  return response.data;
};
